import _ from 'lodash';
import { DatePicker, Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getSteps } from '../../api/courses.api';
import { getSemesters } from '../../api/semesters.api';
import Button from '../../components/Button';
import ErrorResponse from '../../components/ErrorResponse';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Loader from '../../components/Loader';
import MultiCampusModal from '../../components/statistics/regular-test/modals/MultiCampusModal';
import TabItem from '../../components/statistics/TabItem';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import { IGetSteps } from '../../models/courses.model';
import { IGetSemesters, ISemester } from '../../models/semesters.model';
import { RootState } from '../../store/rootReducer';
import { getReverseYears } from '../../lib/util';
import { useLazyGetStatisticsBooksQuery } from '../../api/books/bookManagement.api';
import DownloadButton from '../../components/DownloadButton';
import exportTable from '../../lib/exportTable';
import EduStepSelect from "../../components/common/EduStepSelect";
import LevelSelect from "../../components/statistics/LevelSelect";
import SelectCampusMultiChoice from '../../components/SelectCampusMultiChoice';

enum Contents {
  Summary,
}

const StatisticsBook = () => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0005 = 연도
  const SY0005 = commonCodes.find((item) => item.commonCode === 'SY0005')?.detailCode;
  // PR0002 = 학기유형
  const PR0002 = commonCodes.find((item) => item.commonCode === 'PR0002')?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesters>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetSteps>();
  const { infoNotification } = useNotification();

  const [content, setContent] = useState(Contents.Summary);
  const [semesterCode, setSemesterCode] = useState<number>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [levelCode, setLevelCode] = useState<number>();
  const [courseCode, setCourseCode] = useState<number>();
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [date, setDate] = useState(
    moment().subtract(1, 'days').format('YYYYMMDD'),
  );
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [year, setYear] = useState<string>();
  const [semesterType, setSemesterType] = useState("RG");
  const [semesterData, setSemesterData] = useState<ISemester[]>([]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
    setSemesterCode(undefined);
  }, [year]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
    setSemesterCode(undefined);
  }, [semesterType]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
  }, [res]);

  const [getStatisticsBooksTrigger, { data: statisticsResponse }] =
    useLazyGetStatisticsBooksQuery();
  const statisticsBooks = statisticsResponse?.data;

  const columns: TableColumnsType<any> = [
    {
      title: t('class.class.placeholder1'),
      dataIndex: 'campusName',
      align: 'center',
    },
    {
      title: t('statistics.book.1'),
      dataIndex: 'checkoutBooks',
      align: 'center',
    },
    {
      title: t('statistics.book.2'),
      dataIndex: 'returnBooks',
      align: 'center',
    },
    {
      title: t('statistics.book.3'),
      dataIndex: 'overdueBooks',
      align: 'center',
    },
  ];

  const onExportTable = () => {
    const excelColumns = [
      { title: 'no', dataIndex: 'no', align: 'center' },
      ...columns,
    ];
    const currentDate = moment(new Date());

    const statisticsBooksMap = [
      ...statisticsBooks.map((item: any, index: number) => {
        return {
          ...item,
          no: index,
        };
      }),
      {
        no: statisticsBooks.length,
        campusName: t('statistics.book.4'),
        checkoutBooks: statisticsBooks.reduce(
          (acc: number, cur: any) => (acc += cur?.checkoutBooks),
          0,
        ),
        returnBooks: statisticsBooks.reduce(
          (acc: number, cur: any) => (acc += cur?.returnBooks),
          0,
        ),
        overdueBooks: statisticsBooks.reduce(
          (acc: number, cur: any) => (acc += cur?.overdueBooks),
          0,
        ),
      },
    ];

    exportTable(
      excelColumns,
      statisticsBooksMap,
      `${t('statistics.book.5')}_(${currentDate.format('YYYYMMDD')})`,
      t('statistics.book.6'),
      [10, 20, 20, 20, 20],
    );
  };

  useEffect(() => {
    execute(getSemesters());
    execute2(getSteps());

    defSelectBoxSetting();
  }, []);

  const defSelectBoxSetting = () => {
    //검색 필드 Default값 셋팅
    const defSelValue:any = "";
    setEduStepCode(defSelValue);
    setLevelCode(defSelValue);
    setCourseCode(defSelValue);
  }

  const fnSetSemesterData = (year : string, semesterType : string) => {
    if(res) {
      let data: ISemester[] = _.cloneDeep(res.data);

      if(year) {
        data = data.filter((item: any) => item.learningYearCode === year);
      }

      if(semesterType) {
        data = data.filter((item: any) => item.semesterType === semesterType);
      }

      if(data.length === 0) {
        setSemesterCode(undefined);
      }

      setSemesterData(data);
    }
  }

  return (
    <>
    <SimpleHeader
      iconFile="icon-statistics-big.svg"
      parentMenu={t('common.menu.statistics')}
      currentMenu={t('statistics.book.7')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label> {/* 캠퍼스 */}
            <SelectCampusMultiChoice
              placeholder={t('statistics.eLearning.title2')}
              onChange={setCampusCodes}
              value={campusCodes}
            />
            <label>
              (
              {campusCodes.length
                ? `${
                    campusCodes.length
                  }${t('statistics.attendance.text1')}`
                : t('common.text.all')}
              )
            </label>
          </li>
          <li>
            <label>{t('statistics.refund.placeholder1')}</label> {/* 연도 */}
            <Select
              placeholder={t('statistics.refund.placeholder1')}
              value={year}
              onChange={value => setYear(value)}
            >
              {getReverseYears(SY0005).map(item => (
                <Select.Option
                  key={item.detailCode}
                  value={item.detailCode}
                >
                  {item.korName}
                </Select.Option>
              ))}
            </Select>            
          </li>
            <li>
              <label>
                {t('program.semester.column11')}
              </label>
              <Select
                className="w-120p"
                value={semesterType}
                onChange={value => setSemesterType(value)}
              >
                {
                  PR0002?.map(({korName: label, detailCode: value}) => {
                    return (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    )
                  })
                }
              </Select>
            </li>  
          <li>
            <label>{t('class.class.placeholder4')}</label> {/* 학기 */}
            <Select
              placeholder={t('statistics.regularTest.placeholder2')}
              value={semesterCode}
              onChange={(value) => setSemesterCode(value)}
            >
              {semesterData?.map((item) => (
                  <Select.Option
                    key={item.semesterCode}
                    value={item.semesterCode}
                  >
                    {item.semesterName}
                  </Select.Option>
                ))}
            </Select>
          </li>
          <li>
            <label>{t('common.placeholder.1')}</label> {/* 교육단계 */}
            <EduStepSelect
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);

                const defSelValue:any = "";
                setLevelCode(defSelValue);
                setCourseCode(defSelValue);
              }}
              width={90}
              includeAll
            />
          </li>
          <li>
            <label>{t('student.student.column26')}</label> {/* 레벨 */}
            <div>
              <LevelSelect
                levelCode={levelCode}
                setLevelCode={value =>{
                  setLevelCode(value);

                  const defSelValue:any = "";
                  setCourseCode(defSelValue);
                }}
                data={res2?.data}
                eduStepCode={eduStepCode}
              />
            </div>
          </li>
          <li>
            <label>{t('student.student.column27')}</label> {/* 단계 */}
              <Select                
                placeholder={t('student.student.column27')}
                value={courseCode}
                onChange={(value) => setCourseCode(value)}
                disabled={!levelCode}
              >
                <Select.Option value="">
                  {t('common.text.all')}
                </Select.Option>
                {res2?.data
                  ?.find(
                    (item) => Number(item.courseCode) === levelCode,
                  )
                  ?.step?.map((item) => (
                    <Select.Option
                      key={item.courseCode}
                      value={item.courseCode}
                    >
                      {item.courseName}
                    </Select.Option>
                  ))}
              </Select>
          </li>
          <li>
            <label>{t('statistics.studentEnroll.column5')}</label> {/* 기준일 */}
            <div className="date-picker__area">
              <DatePicker
                value={date ? moment(date) : undefined}
                onChange={(e) => {
                  if (e === null) setDate('');
                  else setDate(moment(e).format('YYYY-MM-DD'));
                }}
                format={['YYYY-MM-DD', 'YYYYMMDD']}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={() => {
                  if (!semesterCode) {
                    return infoNotification(
                      t('statistics.studentEnroll.message1'),
                    );
                  }

                  if (!date) {
                    return infoNotification(
                      t('statistics.studentEnroll.message2'),
                    );
                  }

                  const data = {
                    campusCodes: campusCodes ? campusCodes.join(',') : '',
                    semesterCode,
                    referenceDate: date,
                    courseCode: courseCode ? courseCode : levelCode,
                  };
                  //console.log(data);
                  getStatisticsBooksTrigger(data);
                }}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>	
    <div className="content scrollbar">
      {(isLoading || isLoading2) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <div>
        <div className="comm-tab__line">
          <ul>
            <TabItem
              currentContent={content}
              onSetContent={setContent}
              tabContent={Contents.Summary}
              tabLabel={t('statistics.attendance.Tab1')}
            />
          </ul>
        </div>
        <div>
          <div className="comm-table__area">
            <Table
              columns={columns}
              dataSource={statisticsBooks ?? []}
              summary={(pageData) => {
                let checkoutTotal = pageData.reduce(
                  (acc, cur) => (acc += cur?.checkoutBooks),
                  0,
                );
                let returnTotal = pageData.reduce(
                  (acc, cur) => (acc += cur?.returnBooks),
                  0,
                );
                let overDueTotal = pageData.reduce(
                  (acc, cur) => (acc += cur?.overdueBooks),
                  0,
                );

                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell align="center" index={0}>
                        {t('statistics.book.4')}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="center" index={1}>
                        {checkoutTotal}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="center" index={2}>
                        {returnTotal}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="center" index={3}>
                        {overDueTotal}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                );
              }}
            />
            <div className="comm-bx__between">
              <DownloadButton onClick={onExportTable} />
            </div>
          </div>
        </div>
      </div>

      {multiCampusVisible && (
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      )}
    </div>
    </>
  );
};

export default StatisticsBook;
